import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, Phone, Mail, Globe, Share2, Heart, ChevronLeft,
  ChevronRight, Star, MessageCircle, Send, Loader2, Check,
  Car, Home, Truck, Sofa, Fuel, Gauge, Calendar, Bed, Bath,
  Ruler, Users, X, ExternalLink, Gift, CreditCard, Plane,
  Tag, Clock, Shield, Award, Sparkles, Building, DollarSign,
  CheckCircle, ArrowRight, Play, Image as ImageIcon, Banknote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { useTracking } from "@/contexts/TrackingContext";
import ListingAgentCard from "@/components/marketplace/ListingAgentCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Type configurations
const TYPE_CONFIG = {
  vehicles: {
    name: "Vehicle",
    plural: "Vehicles",
    color: "#EF4444",
    icon: Car,
    heroName: "TONY",
    heroImage: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/6l02z6bb_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_35_53%20PM.png",
  },
  real_estate: {
    name: "Property",
    plural: "Properties",
    color: "#EC4899",
    icon: Home,
    heroName: "MARCUS",
    heroImage: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/xs0rcc5u_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_28_31%20PM.png",
  },
  furniture: {
    name: "Furniture",
    plural: "Furniture",
    color: "#8B5CF6",
    icon: Sofa,
    heroName: "OLIVIA",
    heroImage: "https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/aau7dz9c_ChatGPT%20Image%20Feb%205%2C%202026%2C%2001_38_25%20PM.png",
  },
  rvs: {
    name: "RV",
    plural: "RVs",
    color: "#06B6D4",
    icon: Truck,
    heroName: "RILEY",
    heroImage: "https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/saw7oteq_ChatGPT%20Image%20Feb%205%2C%202026%2C%2001_40_33%20PM.png",
  },
  jobs: {
    name: "Job",
    plural: "Jobs",
    color: "#10B981",
    icon: Building,
    heroName: "JORDAN",
    heroImage: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/6l02z6bb_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_35_53%20PM.png",
  },
  events: {
    name: "Event",
    plural: "Events",
    color: "#F59E0B",
    icon: Calendar,
    heroName: "ELENA",
    heroImage: "https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/aau7dz9c_ChatGPT%20Image%20Feb%205%2C%202026%2C%2001_38_25%20PM.png",
  },
  deals: {
    name: "Deal",
    plural: "Deals",
    color: "#7C3AED",
    icon: Tag,
    heroName: "MAYA",
    heroImage: "https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/saw7oteq_ChatGPT%20Image%20Feb%205%2C%202026%2C%2001_40_33%20PM.png",
  },
};

// High-quality placeholder images from Pexels (more reliable than Unsplash source)
const PLACEHOLDER_IMAGES = {
  vehicles: [
    "https://images.pexels.com/photos/170811/pexels-photo-170811.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/116675/pexels-photo-116675.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/210019/pexels-photo-210019.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1035108/pexels-photo-1035108.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1149137/pexels-photo-1149137.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/919073/pexels-photo-919073.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1545743/pexels-photo-1545743.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1082655/pexels-photo-1082655.jpeg?auto=compress&cs=tinysrgb&w=800",
  ],
  real_estate: [
    "https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/2102587/pexels-photo-2102587.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/2635038/pexels-photo-2635038.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/276724/pexels-photo-276724.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1080721/pexels-photo-1080721.jpeg?auto=compress&cs=tinysrgb&w=800",
  ],
  furniture: [
    "https://images.pexels.com/photos/1350789/pexels-photo-1350789.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1571453/pexels-photo-1571453.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/2062431/pexels-photo-2062431.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1648768/pexels-photo-1648768.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/963486/pexels-photo-963486.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1866149/pexels-photo-1866149.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/2440471/pexels-photo-2440471.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/3049121/pexels-photo-3049121.jpeg?auto=compress&cs=tinysrgb&w=800",
  ],
  rvs: [
    "https://images.pexels.com/photos/2533092/pexels-photo-2533092.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/2662116/pexels-photo-2662116.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/5273008/pexels-photo-5273008.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/4993914/pexels-photo-4993914.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/2533090/pexels-photo-2533090.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/6271625/pexels-photo-6271625.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/2533097/pexels-photo-2533097.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1687093/pexels-photo-1687093.jpeg?auto=compress&cs=tinysrgb&w=800",
  ],
  jobs: [
    "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/3184287/pexels-photo-3184287.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1181533/pexels-photo-1181533.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/3182781/pexels-photo-3182781.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1181622/pexels-photo-1181622.jpeg?auto=compress&cs=tinysrgb&w=800",
  ],
  events: [
    "https://images.pexels.com/photos/2747449/pexels-photo-2747449.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/2263436/pexels-photo-2263436.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/2608517/pexels-photo-2608517.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1190297/pexels-photo-1190297.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/2774556/pexels-photo-2774556.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/976866/pexels-photo-976866.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/2306281/pexels-photo-2306281.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/1540406/pexels-photo-1540406.jpeg?auto=compress&cs=tinysrgb&w=800",
  ],
  deals: [
    "https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/5632381/pexels-photo-5632381.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/3962294/pexels-photo-3962294.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/5632371/pexels-photo-5632371.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/5650026/pexels-photo-5650026.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/5632383/pexels-photo-5632383.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/4968391/pexels-photo-4968391.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/3965545/pexels-photo-3965545.jpeg?auto=compress&cs=tinysrgb&w=800",
  ],
};

// Generate gallery images
function generateGalleryImages(listing, type, count = 8) {
  const typeImages = PLACEHOLDER_IMAGES[type] || PLACEHOLDER_IMAGES.vehicles;
  const images = [];
  
  // Add listing images first if available
  if (listing.images && listing.images.length > 0) {
    images.push(...listing.images);
  }
  
  // Fill with placeholder images
  while (images.length < count) {
    const idx = images.length % typeImages.length;
    images.push(typeImages[idx]);
  }
  
  return images.slice(0, count);
}

// Image Gallery Component
function ImageGallery({ images, title }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  
  const nextImage = () => setCurrentIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  
  const handleImageLoad = (idx) => {
    setLoadedImages(prev => ({ ...prev, [idx]: true }));
  };
  
  return (
    <>
      {/* Main Image */}
      <div className="relative aspect-[16/10] rounded-xl overflow-hidden bg-gradient-to-br from-[#1E293B] to-[#0F172A] mb-4">
        <img
          src={images[currentIndex]}
          alt={`${title} - Image ${currentIndex + 1}`}
          className="w-full h-full object-cover cursor-pointer"
          onClick={() => setIsLightboxOpen(true)}
          onLoad={() => handleImageLoad(currentIndex)}
        />
        
        {/* Navigation arrows */}
        <button
          onClick={prevImage}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextImage}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
        
        {/* Image counter */}
        <div className="absolute bottom-3 right-3 px-3 py-1 rounded-full bg-black/50 text-white text-sm flex items-center gap-2">
          <ImageIcon className="w-4 h-4" />
          {currentIndex + 1} / {images.length}
        </div>
        
        {/* Click to expand */}
        <div className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-black/50 text-white text-sm">
          Click to view full gallery
        </div>
      </div>
      
      {/* Thumbnail Strip */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {images.map((img, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all bg-[#1E293B] ${
              idx === currentIndex ? "border-[#7C3AED] scale-105" : "border-transparent opacity-70 hover:opacity-100"
            }`}
          >
            <img 
              src={img} 
              alt={`Thumb ${idx + 1}`} 
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ))}
      </div>
      
      {/* Lightbox */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setIsLightboxOpen(false)}
          >
            <button
              onClick={() => setIsLightboxOpen(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
            >
              <X className="w-6 h-6" />
            </button>
            
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            
            <img
              src={images[currentIndex]}
              alt={`${title} - Image ${currentIndex + 1}`}
              className="max-h-[85vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
            
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-lg">
              {currentIndex + 1} / {images.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// AI Chat Component
function AIChat({ listing, config }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `listing-${listing.id}-${Date.now()}`);
  const messagesEndRef = useRef(null);
  
  useEffect(() => {
    const title = listing.title || `${listing.year || ''} ${listing.make || ''} ${listing.model || ''}`.trim();
    const greeting = `Hi! I'm ${config.heroName}, your AI ${config.name} Expert! 🚀\n\nI see you're interested in this ${title}. I can answer any questions you have!\n\n• Ask me about features, specifications, or condition\n• I can help you schedule a viewing\n• Want to know about financing options?\n• Ask about our exclusive travel savings card!\n\nHow can I help you today?`;
    setMessages([{ role: "assistant", content: greeting }]);
  }, [listing, config]);
  
  useEffect(() => {
    // Only scroll within the chat container, not the whole page
    // Using block: 'nearest' prevents page-level scrolling
    if (messages.length > 1) {  // Only auto-scroll after initial greeting
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [messages]);
  
  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = inputValue.trim();
    setInputValue("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId,
        business_id: listing.business_id || "marketplace",
        context: JSON.stringify({
          listing_type: listing.listing_type,
          listing_title: listing.title,
          listing_price: listing.price,
          listing_location: `${listing.city}, ${listing.state}`,
        }),
      });
      setMessages((prev) => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      setMessages((prev) => [...prev, { 
        role: "assistant", 
        content: "I'm having a brief connection issue, but I'm still here to help! Feel free to call us directly or ask me another question." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="glass-card rounded-xl overflow-hidden" style={{ borderColor: `${config.color}30` }}>
      {/* Header */}
      <div 
        className="p-4 flex items-center gap-3 border-b border-white/10"
        style={{ background: `linear-gradient(135deg, ${config.color}20, transparent)` }}
      >
        <img
          src={config.heroImage}
          alt={config.heroName}
          className="w-12 h-12 rounded-full border-2 object-cover"
          style={{ borderColor: config.color }}
        />
        <div>
          <h3 className="text-white font-bold">{config.heroName}</h3>
          <p className="text-sm" style={{ color: config.color }}>AI {config.name} Expert™</p>
        </div>
        <div className="ml-auto flex items-center gap-1 text-[#10B981] text-sm">
          <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
          Online
        </div>
      </div>
      
      {/* Messages */}
      <div className="h-72 overflow-y-auto p-4 space-y-3">
        {messages.map((msg, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] p-3 rounded-2xl text-sm whitespace-pre-wrap ${
                msg.role === "user"
                  ? "text-white rounded-br-sm"
                  : "bg-white/10 text-[#E2E8F0] rounded-bl-sm"
              }`}
              style={msg.role === "user" ? { backgroundColor: `${config.color}60` } : {}}
            >
              {msg.content}
            </div>
          </motion.div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white/10 p-3 rounded-2xl rounded-bl-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: config.color }} />
              <span className="text-[#94A3B8] text-sm">{config.heroName} is typing...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Quick Actions */}
      <div className="px-4 py-2 border-t border-white/5 flex flex-wrap gap-2">
        {["Is this still available?", "Schedule a viewing", "Financing options?"].map((q) => (
          <button
            key={q}
            onClick={() => { setInputValue(q); }}
            className="px-3 py-1 rounded-full text-xs border transition-colors hover:bg-white/5"
            style={{ borderColor: `${config.color}50`, color: config.color }}
          >
            {q}
          </button>
        ))}
      </div>
      
      {/* Input */}
      <div className="p-4 border-t border-white/10 bg-white/5">
        <div className="flex items-center gap-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && sendMessage()}
            placeholder={`Ask ${config.heroName} anything...`}
            className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
          />
          <Button
            onClick={sendMessage}
            disabled={isLoading || !inputValue.trim()}
            style={{ backgroundColor: config.color }}
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Reviews Component
function ReviewsSection({ listing, config }) {
  const mockReviews = [
    {
      id: 1,
      name: "Michael T.",
      rating: 5,
      date: "2 weeks ago",
      comment: "Excellent experience! The listing was exactly as described. Very professional and helpful throughout the entire process.",
      verified: true,
    },
    {
      id: 2,
      name: "Sarah L.",
      rating: 5,
      date: "1 month ago",
      comment: "Outstanding service! They went above and beyond to help me find exactly what I was looking for. Highly recommend!",
      verified: true,
    },
    {
      id: 3,
      name: "David K.",
      rating: 4,
      date: "1 month ago",
      comment: "Great selection and fair pricing. The AI chat feature was super helpful in answering my questions quickly.",
      verified: true,
    },
  ];
  
  return (
    <div className="glass-card rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <Star className="w-5 h-5" style={{ color: config.color }} />
          Customer Reviews
        </h3>
        <div className="flex items-center gap-2">
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star key={star} className="w-5 h-5 fill-[#F59E0B] text-[#F59E0B]" />
            ))}
          </div>
          <span className="text-white font-bold">4.9</span>
          <span className="text-[#94A3B8]">(127 reviews)</span>
        </div>
      </div>
      
      <div className="space-y-4">
        {mockReviews.map((review) => (
          <div key={review.id} className="p-4 bg-white/5 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#00E5FF] flex items-center justify-center text-white font-bold">
                  {review.name.charAt(0)}
                </div>
                <div>
                  <p className="text-white font-semibold flex items-center gap-2">
                    {review.name}
                    {review.verified && (
                      <Badge className="bg-[#10B981]/20 text-[#10B981] text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />Verified
                      </Badge>
                    )}
                  </p>
                  <p className="text-sm text-[#64748B]">{review.date}</p>
                </div>
              </div>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${star <= review.rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#64748B]"}`}
                  />
                ))}
              </div>
            </div>
            <p className="text-[#94A3B8]">{review.comment}</p>
          </div>
        ))}
      </div>
      
      <Button variant="outline" className="w-full mt-4 border-white/10 text-white hover:bg-white/10">
        View All 127 Reviews
      </Button>
    </div>
  );
}

// Special Offers Component - UPDATED: Removed $120k funding, added proper links
function SpecialOffers({ config, listing }) {
  return (
    <div className="glass-card rounded-xl p-6 border-2" style={{ borderColor: `${config.color}30` }}>
      <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <Gift className="w-5 h-5" style={{ color: config.color }} />
        Exclusive Buyer Perks
      </h3>
      
      <div className="space-y-4">
        {/* Travel Savings Card */}
        <motion.div 
          className="p-4 rounded-lg bg-gradient-to-r from-[#F59E0B]/20 to-[#F59E0B]/5 border border-[#F59E0B]/30"
          whileHover={{ scale: 1.02 }}
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center flex-shrink-0">
              <Plane className="w-6 h-6 text-[#F59E0B]" />
            </div>
            <div>
              <h4 className="text-white font-bold">FREE Resort Stay & Travel Savings Card</h4>
              <p className="text-sm text-[#94A3B8] mb-2">
                Get a complimentary 3-day/2-night resort stay + exclusive travel discounts worth $2,500!
              </p>
              <Badge className="bg-[#F59E0B] text-black font-bold">$0 with any purchase</Badge>
            </div>
          </div>
        </motion.div>
        
        {/* Get Loan Ready - Now scrolls to chat for lead capture */}
        <div 
          onClick={() => {
            document.getElementById('ai-chat-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Focus the chat input after scrolling
            setTimeout(() => {
              document.querySelector('#ai-chat-section input')?.focus();
            }, 500);
          }}
          className="block cursor-pointer"
        >
          <motion.div 
            className="p-4 rounded-lg bg-gradient-to-r from-[#10B981]/20 to-[#10B981]/5 border border-[#10B981]/30"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-lg bg-[#10B981]/20 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-[#10B981]" />
              </div>
              <div>
                <h4 className="text-white font-bold flex items-center gap-2">
                  Get Financing Options
                  <MessageCircle className="w-4 h-4 text-[#10B981]" />
                </h4>
                <p className="text-sm text-[#94A3B8] mb-2">
                  Chat with us to explore the best financing options for this listing!
                </p>
                <Badge className="bg-[#10B981] text-black font-bold">Ask About Financing</Badge>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Get Loan Ready - Credit Boost Link */}
        <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-[#10B981]/10 to-[#059669]/10 border border-[#10B981]/30">
          <p className="text-sm text-[#94A3B8] mb-3 text-center">
            Have credit issues or need a credit score boost first before applying?
          </p>
          <a 
            href="https://creditfirminc.net/credit-boost-624266?am_id=roehubbard3909"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full h-11 rounded-lg bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-bold transition-all shadow-lg shadow-[#10B981]/20 hover:shadow-[#10B981]/40"
          >
            <CreditCard className="w-5 h-5" />
            Get Loan Ready
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}

// Main Inventory Detail Page
export default function InventoryDetailPage() {
  const { type, id } = useParams();
  const [listing, setListing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const { trackViewContent, setBusinessContext } = useTracking();
  
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.vehicles;
  const TypeIcon = config.icon;
  
  // Scroll to top immediately on mount/navigation - use useLayoutEffect for synchronous scroll
  useLayoutEffect(() => {
    // Temporarily disable smooth scroll behavior
    const htmlElement = document.documentElement;
    htmlElement.style.scrollBehavior = 'auto';
    
    window.scrollTo(0, 0);
    htmlElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Restore after brief delay
    const timeout = setTimeout(() => {
      htmlElement.style.scrollBehavior = '';
    }, 100);
    
    return () => clearTimeout(timeout);
  }, [type, id]);
  
  useEffect(() => {
    const fetchListing = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API}/marketplace-listings/${id}`);
        setListing(response.data);
      } catch (error) {
        console.error("Error fetching listing:", error);
        try {
          const response = await axios.get(`${API}/inventory/${type}/${id}`);
          setListing(response.data);
        } catch (err) {
          console.error("Error fetching from inventory:", err);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchListing();
  }, [type, id]);

  // Track ViewContent when listing loads
  useEffect(() => {
    if (listing && !isLoading) {
      // Set business context for pixel isolation (if listing has associated business)
      if (listing.business_id) {
        setBusinessContext({ id: listing.business_id, tracking: listing.tracking });
      }
      
      // Fire ViewContent event
      trackViewContent({
        content_id: listing.id || id,
        content_type: type,
        content_name: listing.title || listing.name,
        business_id: listing.business_id || 'marketplace',
        business_name: listing.seller_name || listing.business_name || 'LocalBest.ai Marketplace',
        value: listing.price,
        price: listing.price,
        currency: 'USD',
        category: listing.listing_type || type,
        listing_type: type,
        // Vehicle-specific
        make: listing.make,
        model: listing.model,
        year: listing.year,
        mileage: listing.mileage,
        // Real estate-specific
        bedrooms: listing.bedrooms,
        bathrooms: listing.bathrooms,
        sqft: listing.sqft,
        // Job-specific
        company: listing.company,
        salary: listing.salary,
        // Common
        location: listing.location,
        condition: listing.condition,
      });
    }
  }, [listing, isLoading, id, type, trackViewContent, setBusinessContext]);
  
  if (isLoading) {
    return (
      <div className="page-container min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: config.color }} />
      </div>
    );
  }
  
  if (!listing) {
    return (
      <div className="page-container min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <TypeIcon className="w-20 h-20 mx-auto mb-6 text-[#64748B]" />
          <h1 className="text-3xl font-bold text-white mb-4">Listing Not Found</h1>
          <p className="text-[#94A3B8] mb-8">
            This listing may have been sold or is no longer available.
          </p>
          <Link to={`/marketplace/${type}`}>
            <Button style={{ backgroundColor: config.color }}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to {config.name}s
            </Button>
          </Link>
        </div>
      </div>
    );
  }
  
  const galleryImages = generateGalleryImages(listing, type, 8);
  const title = listing.title || `${listing.year || ''} ${listing.make || ''} ${listing.model || ''}`.trim();
  
  // Type-specific details
  const getDetails = () => {
    switch (type) {
      case "vehicles":
        return [
          { label: "Year", value: listing.year },
          { label: "Make", value: listing.make },
          { label: "Model", value: listing.model },
          { label: "Mileage", value: listing.mileage !== null && listing.mileage !== undefined ? `${listing.mileage.toLocaleString()} mi` : "N/A" },
          { label: "Condition", value: listing.condition },
          { label: "Body Style", value: listing.body_style },
          { label: "Transmission", value: listing.transmission },
          { label: "Drivetrain", value: listing.drivetrain },
          { label: "Fuel Type", value: listing.fuel_type },
          { label: "Exterior Color", value: listing.exterior_color },
          { label: "VIN", value: listing.vin },
        ];
      case "real_estate":
        return [
          { label: "Property Type", value: listing.property_type },
          { label: "Status", value: listing.listing_status?.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase()) },
          { label: "Bedrooms", value: listing.bedrooms },
          { label: "Bathrooms", value: listing.bathrooms },
          { label: "Square Feet", value: listing.sqft?.toLocaleString() },
          { label: "Lot Size", value: listing.lot_size ? `${listing.lot_size} acres` : "N/A" },
          { label: "Year Built", value: listing.year_built },
          { label: "Address", value: listing.address },
        ];
      case "rvs":
        return [
          { label: "Year", value: listing.year },
          { label: "Make", value: listing.make },
          { label: "RV Type", value: listing.rv_type },
          { label: "Condition", value: listing.condition },
          { label: "Sleeps", value: listing.sleeps },
          { label: "Length", value: listing.length ? `${listing.length} ft` : "N/A" },
          { label: "Slide-outs", value: listing.slideouts },
          { label: "Mileage", value: listing.mileage ? `${listing.mileage.toLocaleString()} mi` : "N/A" },
        ];
      case "furniture":
        return [
          { label: "Category", value: listing.category },
          { label: "Subcategory", value: listing.subcategory },
          { label: "Style", value: listing.style },
          { label: "Material", value: listing.material },
          { label: "Room Type", value: listing.room_type },
          { label: "Condition", value: listing.condition },
        ];
      default:
        return [];
    }
  };
  
  return (
    <div className="page-container min-h-screen overflow-x-hidden">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">
        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm overflow-x-auto">
          <Link to="/marketplace" className="text-[#94A3B8] hover:text-white whitespace-nowrap">Marketplace</Link>
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-[#64748B] flex-shrink-0" />
          <Link to={`/marketplace/${type}`} className="hover:text-white whitespace-nowrap" style={{ color: config.color }}>
            {config.plural}
          </Link>
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-[#64748B] flex-shrink-0" />
          <span className="text-white truncate max-w-[120px] sm:max-w-[200px]">{title}</span>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pb-8 sm:pb-12">
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6 min-w-0">
            {/* Gallery */}
            <ImageGallery images={galleryImages} title={title} />
            
            {/* Title & Price Section */}
            <div className="glass-card rounded-xl p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between gap-4 mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {listing.condition && (
                      <Badge className={`${
                        listing.condition.toLowerCase() === "new" ? "bg-[#10B981]" :
                        listing.condition.toLowerCase().includes("certified") ? "bg-[#F59E0B]" :
                        "bg-[#64748B]"
                      }`}>
                        {listing.condition}
                      </Badge>
                    )}
                    {listing.featured && (
                      <Badge className="bg-[#7C3AED]">
                        <Sparkles className="w-3 h-3 mr-1" />Featured
                      </Badge>
                    )}
                  </div>
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 break-words">{title}</h1>
                  <div className="flex items-center gap-2 text-[#94A3B8] text-sm sm:text-base">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{listing.city}, {listing.state}</span>
                  </div>
                </div>
                <div className="sm:text-right">
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold" style={{ color: config.color }}>
                    {listing.price ? `$${listing.price.toLocaleString()}` : "Call for Price"}
                    {type === "real_estate" && listing.listing_status === "for_rent" && (
                      <span className="text-base sm:text-lg font-normal text-[#94A3B8]">/mo</span>
                    )}
                  </p>
                  <p className="text-xs sm:text-sm text-[#64748B]">Est. payment available</p>
                </div>
              </div>
              
              {/* Quick Stats Bar */}
              <div className="flex flex-wrap gap-3 sm:gap-4 py-3 sm:py-4 border-t border-b border-white/10 text-sm sm:text-base">
                {type === "vehicles" && (
                  <>
                    {listing.mileage !== null && listing.mileage !== undefined && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Gauge className="w-4 h-4 sm:w-5 sm:h-5 text-[#00E5FF] flex-shrink-0" />
                        <span className="text-white">{listing.mileage.toLocaleString()} mi</span>
                      </div>
                    )}
                    {listing.fuel_type && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Fuel className="w-4 h-4 sm:w-5 sm:h-5 text-[#10B981] flex-shrink-0" />
                        <span className="text-white">{listing.fuel_type}</span>
                      </div>
                    )}
                    {listing.transmission && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Car className="w-4 h-4 sm:w-5 sm:h-5 text-[#F59E0B] flex-shrink-0" />
                        <span className="text-white">{listing.transmission}</span>
                      </div>
                    )}
                  </>
                )}
                {type === "real_estate" && (
                  <>
                    {listing.bedrooms && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Bed className="w-4 h-4 sm:w-5 sm:h-5 text-[#EC4899] flex-shrink-0" />
                        <span className="text-white">{listing.bedrooms} Beds</span>
                      </div>
                    )}
                    {listing.bathrooms && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Bath className="w-4 h-4 sm:w-5 sm:h-5 text-[#00E5FF] flex-shrink-0" />
                        <span className="text-white">{listing.bathrooms} Baths</span>
                      </div>
                    )}
                    {listing.sqft && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Ruler className="w-4 h-4 sm:w-5 sm:h-5 text-[#10B981] flex-shrink-0" />
                        <span className="text-white">{listing.sqft.toLocaleString()} sqft</span>
                      </div>
                    )}
                  </>
                )}
                {type === "rvs" && (
                  <>
                    {listing.sleeps && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Users className="w-4 h-4 sm:w-5 sm:h-5 text-[#06B6D4] flex-shrink-0" />
                        <span className="text-white">Sleeps {listing.sleeps}</span>
                      </div>
                    )}
                    {listing.length && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Ruler className="w-4 h-4 sm:w-5 sm:h-5 text-[#10B981] flex-shrink-0" />
                        <span className="text-white">{listing.length} ft</span>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Action Buttons - Mobile responsive */}
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3 mt-4">
                <a href={`tel:${listing.contact_phone || '833-772-7397'}`} className="col-span-1 sm:flex-1 sm:min-w-[140px]">
                  <Button 
                    className="w-full h-10 sm:h-12 text-sm sm:text-lg font-bold"
                    style={{ backgroundColor: config.color }}
                  >
                    <Phone className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="truncate">Call Now</span>
                  </Button>
                </a>
                <Button 
                  onClick={() => {
                    // Scroll to chat section
                    document.getElementById('ai-chat-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                  className="col-span-1 sm:flex-1 sm:min-w-[140px] w-full h-10 sm:h-12 text-sm sm:text-lg font-bold bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] hover:opacity-90"
                >
                  <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  <span className="truncate">Chat</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsSaved(!isSaved)}
                  className={`h-10 sm:h-12 px-3 sm:px-4 ${isSaved ? "bg-[#EC4899]/20 border-[#EC4899] text-[#EC4899]" : "border-white/20 text-white hover:bg-white/10"}`}
                >
                  <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${isSaved ? "fill-[#EC4899]" : ""}`} />
                </Button>
                <Button
                  variant="outline"
                  className="h-10 sm:h-12 px-3 sm:px-4 border-white/20 text-white hover:bg-white/10"
                  onClick={() => {
                    navigator.share?.({
                      title: title,
                      text: listing.description,
                      url: window.location.href,
                    }).catch(() => {
                      navigator.clipboard.writeText(window.location.href);
                    });
                  }}
                >
                  <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </div>
            </div>
            
            {/* Description */}
            <div className="glass-card rounded-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                <TypeIcon className="w-5 h-5 flex-shrink-0" style={{ color: config.color }} />
                Description
              </h2>
              <p className="text-[#94A3B8] leading-relaxed whitespace-pre-wrap text-sm sm:text-base break-words">
                {listing.description}
              </p>
            </div>
            
            {/* Details Grid */}
            <div className="glass-card rounded-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 flex-shrink-0" style={{ color: config.color }} />
                {config.name} Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {getDetails().filter(d => d.value).map((detail, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 border-b border-white/5 gap-2">
                    <span className="text-[#94A3B8] text-sm flex-shrink-0">{detail.label}</span>
                    <span className="text-white font-medium text-sm text-right truncate">{detail.value}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Reviews */}
            <ReviewsSection listing={listing} config={config} />
          </div>
          
          {/* Sidebar - Shows after main content on mobile, beside it on desktop */}
          <div className="space-y-4 sm:space-y-6 lg:order-none">
            {/* AI Chat */}
            <div id="ai-chat-section">
              <AIChat listing={listing} config={config} />
            </div>
            
            {/* Agent + Brokerage Card (with type-appropriate lead CTA → GHL sync) */}
            <ListingAgentCard listing={listing} accentColor={config.color} />
            
            {/* Special Offers - UPDATED */}
            <SpecialOffers config={config} listing={listing} />
            
            {/* Seller Coupons/Deals */}
            {listing.coupons && listing.coupons.length > 0 ? (
              <div className="glass-card rounded-xl p-6 border-2 border-[#10B981]/30">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-[#10B981]" />
                  Seller Discounts
                </h3>
                <div className="space-y-3">
                  {listing.coupons.map((coupon, idx) => (
                    <motion.div 
                      key={idx}
                      className="p-3 bg-[#10B981]/10 rounded-lg border border-[#10B981]/30 cursor-pointer"
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-bold">{coupon.title}</p>
                          <p className="text-xs text-[#94A3B8]">{coupon.description}</p>
                        </div>
                        <Badge className="bg-[#10B981]">{coupon.code}</Badge>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="glass-card rounded-xl p-6 border-2 border-[#10B981]/30">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-[#10B981]" />
                  Available Discounts
                </h3>
                <div className="space-y-3">
                  <motion.div 
                    className="p-3 bg-[#10B981]/10 rounded-lg border border-[#10B981]/30"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-bold">$500 OFF</p>
                        <p className="text-xs text-[#94A3B8]">First-time buyer discount</p>
                      </div>
                      <Badge className="bg-[#10B981]">SAVE500</Badge>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="p-3 bg-[#F59E0B]/10 rounded-lg border border-[#F59E0B]/30"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-bold">FREE Delivery</p>
                        <p className="text-xs text-[#94A3B8]">Within 50 miles</p>
                      </div>
                      <Badge className="bg-[#F59E0B] text-black">FREEDEL</Badge>
                    </div>
                  </motion.div>
                </div>
              </div>
            )}
            
            {/* Trust Badges */}
            <div className="glass-card rounded-xl p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-[#10B981]/20 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-[#10B981]" />
                  </div>
                  <p className="text-white text-sm font-medium">Verified Listing</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-[#00E5FF]/20 flex items-center justify-center">
                    <Award className="w-6 h-6 text-[#00E5FF]" />
                  </div>
                  <p className="text-white text-sm font-medium">Top Rated</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-[#7C3AED]/20 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-[#7C3AED]" />
                  </div>
                  <p className="text-white text-sm font-medium">Secure Purchase</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-[#F59E0B]/20 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[#F59E0B]" />
                  </div>
                  <p className="text-white text-sm font-medium">24/7 Support</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
